﻿/**
 * App.js - entry shell: routing, form state (factFind), layout (MainAppLayout), and page composition.
 * Calculations and constants live in src/calculations and src/constants; feature UI in src/features and src/pages.
 * See REFACTOR_STATUS.md and ARCHITECTURE.md for structure and next priorities.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle, Bot, AlertTriangle, X } from 'lucide-react';
import AgedCarePage from './src/features/agedCare/AgedCarePage.tsx';
import AgedCareAnalyser from './src/features/agedCare/AgedCareAnalyser.tsx';
import { ACAT_LEVEL_DATA, DEFAULT_PROJECTION_YEARS, INITIAL_FACT_FIND } from './src/constants/index.ts';
import { freshCoupleValidation } from './src/constants/coupleValidationDefaults.ts';
import SupportAtHomePage from './src/features/supportAtHome/SupportAtHomePage.tsx';
import RetirementVillagePage from './src/features/retirementVillage/RetirementVillagePage.tsx';
import GrannyFlatPage from './src/features/grannyFlat/GrannyFlatPage.tsx';
import DownsizerPage from './src/features/downsizer/DownsizerPage.tsx';
import CashflowPensionPage from './src/features/cashflowPension/CashflowPensionPage.tsx';
import { incomeOnlySetter } from './src/components/strategy/IncomeOnlyTick.tsx';
import { guaranteedIncomeGoalsSetter, cashflowGoalsSetter } from './src/features/cashflowPension/cashflowPensionScenario.ts';
import { rearmAdviserIntro } from './src/components/narrative/index.tsx';
import { AWiseWordmark } from './src/components/branding/index.tsx';
import Dashboard from './src/pages/Dashboard/index.tsx';
import AssumptionsPage from './src/pages/AssumptionsPage.tsx';
import PlaceholderPage from './src/pages/PlaceholderPage.tsx';
import { getIsPlatformAdmin, getAdminAccess } from './src/services/adminApi.ts';
import { clearPartnerWizardDraft } from './src/features/professionalPortal/PartnerOnboardingWizard.tsx';
import { startCheckout, openBillingPortal } from './src/services/billingApi.ts';
import ClientProfile from './src/pages/ClientProfile/index.tsx';
import PartnerPortal from './src/features/professionalPortal/PartnerPortal.tsx';
import { useProfessionalPortal } from './src/features/professionalPortal/useProfessionalPortal.ts';
import { useSessionAndAuth } from './src/hooks/useSessionAndAuth.ts';
import { useAutoSave } from './src/hooks/useAutoSave.ts';
import AppRouter from './src/routes/AppRouter.tsx';
import { sendMessageToGemini } from './src/services/geminiChat.ts';
import { getSessionUser } from './src/services/authClient.ts';
import { takePendingInvite, finishInviteRedemption, acceptInvite } from './src/services/tenantApi.ts';
import { registerSoa, registerOwnSoa, saveModel, type AdviceModule } from './src/services/adviceApi.ts';
import { loadLatestClientProfile } from './src/services/profileApi.ts';
import AccountModal from './src/components/auth/AccountModal.tsx';
import {
  buildInAppSystemInstruction,
  IN_APP_AI_WELCOME,
} from './src/constants/agedCareWiseAiPrompts.ts';
import AgedCareWiseAiChatPanel from './src/components/chat/AgedCareWiseAiChatPanel.tsx';
import { getAgedCareScreenContext } from './src/features/agedCare/agedCareScreenContext.ts';
import SmallScreenNotice from './src/components/SmallScreenNotice.tsx';
import type { Dispatch, SetStateAction } from 'react';
import type { DraftFactFind } from './src/constants/initialFactFind.ts';
import type { SessionUser } from './src/services/authClient.ts';
import type { GeminiChatTurn } from './src/services/geminiChat.ts';
import type { SavedStrategy, SavedCaseStudy } from './src/pages/Dashboard/dashboardConfig.tsx';
import type {
  CoupleValidationState,
  SavedAgedCareStrategy,
  SavedCaseStudy as AgedCareSavedCaseStudy,
} from './src/features/agedCare/AgedCareAnalyser.tsx';
import type { EvaluatedStrategy } from './src/features/agedCare/futureLifeEventUtils.tsx';
import type { SoaScenario, BillingSummary as PartnerPortalBillingSummary } from './src/features/professionalPortal/PartnerPortal.tsx';
import type { SavedStrategy as RvSavedStrategy } from './src/features/retirementVillage/RetirementVillagePage.tsx';
import PrivacyPolicyUpdateNotice from './src/components/legal/PrivacyPolicyUpdateNotice.tsx';
import { NoticeHost } from './src/components/ui/notices.tsx';
import type {
  LoadedClientProfile,
} from './src/services/profileApi.ts';
import { getModuleScreenContext } from './src/utils/moduleScreenContext.ts';

/** Each module's page, as AWise's screen key, when no plans are on screen yet. */
const MODULE_SCREEN_KEYS: Record<string, string> = { HomeCarePage: 'support-at-home', RetirementVillagePage: 'retirement-village', DownsizingPage: 'downsizing', GrannyFlatPage: 'granny-flat', CashflowPensionPage: 'cashflow-pension' };


// --- FINANCIAL & TAX: pension, tax, gifting, etc. from src/constants/ratesAndThresholds.js (imported above) ---
// --- AGED CARE: single source from Schedule (agedCareSchedule.js) ---

// --- INSIGHTS CONFIG (GOAL_INSIGHTS, FAMILY_ESTATE_INSIGHTS in src/constants/insightsConfig.js) ---
// --- INITIAL STATE & CONFIG (INITIAL_FACT_FIND, OTHER_INCOME_TYPES, ASFA_BUDGET in src/constants) ---
export { ACAT_LEVEL_DATA };

// --- FINANCIAL CALCULATION ENGINE: uses imported calculateFinancials from FinancialCalculations.js ---

// --- MAIN APP COMPONENT ---
function App() {
    const [activePage, setActivePage] = useState('LandingPage');
    /*
     * Which door the Cashflow & Age Pension page was opened through, so its Back returns there
     * (review, 18 Oct 2026: Back always went to Support at home). Worked out during render from the
     * page before it, so the page's first paint already knows; a re-render on the same page changes
     * nothing.
     */
    const lastPageRef = useRef(activePage);
    const cashflowCameFromRef = useRef<'support_at_home' | 'home'>('home');
    if (lastPageRef.current !== activePage) {
        if (activePage === 'CashflowPensionPage') cashflowCameFromRef.current = lastPageRef.current === 'HomeCarePage' ? 'support_at_home' : 'home';
        lastPageRef.current = activePage;
    }
    const [showGeneralAdvice, setShowGeneralAdvice] = useState<string | null>(null);
    const [showSecurity, setShowSecurity] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showCost, setShowCost] = useState(false);
    const [showExperience, setShowExperience] = useState(false);
    const [showFaqs, setShowFaqs] = useState(false);
    // Platform owner/super-admin console — only ever shown when the SERVER confirms admin rights.
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    // Licensee oversight (advice under our licence): who may open it, and whether it is open.
    const [canReviewAdvice, setCanReviewAdvice] = useState(false);
    const [showCompliance, setShowCompliance] = useState(false);
    // The API docs are a sub-screen of the admin console (linked from it, not the sidebar) —
    // only meaningful while showAdmin is true; closeAdmin() below resets both together so a fresh
    // "Admin" entry always lands back on the firms list, not wherever the last visit left off.
    const [showApiDocs, setShowApiDocs] = useState(false);
    const closeAdmin = () => { setShowAdmin(false); setShowApiDocs(false); };
    /*
     * True when the admin console was opened as the admin's HOME — they signed in and have no firm,
     * so routePlatformAdminWithoutFirm put them here. "Back" from there landed on their own empty
     * personal client file, which made no sense (owner, 1 Oct 2026); the console's link becomes
     * "Sign out" instead. Opened from the sidebar or the portal, it stays "Back".
     */
    const [adminIsHome, setAdminIsHome] = useState(false);
    // Consumer paywall (Option A). Only ever shown when billing is LIVE and the consumer has no
    // active access — while billing is off the server reports hasAccess:true and this stays false.
    const [showPaywall, setShowPaywall] = useState(false);
    // Partner-channel entry (real accounts): the two-door chooser + the onboarding wizard.
    const [showPartnerEntry, setShowPartnerEntry] = useState(false);
    // Who is signed in (null when signed out) — drives auto-save and the portal's real mode.
    const [accountUser, setAccountUser] = useState<SessionUser | null>(null);

    // Ask the server whether this signed-in user is a platform admin (drives the admin entry point).
    // The server re-checks on every admin call regardless — this only decides whether to show it.
    useEffect(() => {
        if (!accountUser) {
            setIsPlatformAdmin(false);
            setShowAdmin(false);
            setCanReviewAdvice(false);
            setShowCompliance(false);
            setAdminIsHome(false);
            return;
        }
        let live = true;
        getAdminAccess().then((access) => {
            if (!live) return;
            setIsPlatformAdmin(access.isAdmin);
            setCanReviewAdvice(access.canReviewAdvice);
        });
        return () => { live = false; };
    }, [accountUser]);

    /*
     * A PLATFORM ADMIN IS NEVER OFFERED "BECOME A PARTNER" (owner-reported, 1 Oct 2026).
     *
     * Signing in through the professionals door, or opening the portal, with no firm membership
     * opens the partner onboarding wizard. A platform admin usually has no firm, so the owner signed
     * in successfully and was asked to create a partner profile — while a fresh tab showed the
     * dashboard with the Admin item, proving the sign-in had worked. Every automatic opener of the
     * wizard now asks this first; when the server confirms admin rights the admin console opens
     * instead. The explicit "Become a partner" button is left alone: clicking it is a choice.
     */
    const routePlatformAdminWithoutFirm = async (): Promise<boolean> => {
        const admin = await getIsPlatformAdmin().catch(() => false);
        if (!admin) return false;
        clearPartnerWizardDraft();
        setIsPlatformAdmin(true);
        setShowAdmin(true);
        setAdminIsHome(true);
        setActivePage('Dashboard');
        return true;
    };

    const [factFind, setFactFind] = useState<DraftFactFind>(() => structuredClone(INITIAL_FACT_FIND));
    const [strategyOptions, setStrategyOptions] = useState<Record<string, unknown>>({});
    const [selectedStrategy, setSelectedStrategy] = useState<EvaluatedStrategy | null>(null);
    const [clientProfileStep, setClientProfileStep] = useState(1);
    /** Seeds AgedCareAnalyser's own local legacy-years state; that component owns the value from
     *  here on (see AgedCareAnalyserProps.legacyYears/setLegacyYears in AgedCareAnalyser.tsx). */
    const [legacyYears, setLegacyYears] = useState(DEFAULT_PROJECTION_YEARS);
    
    // Scroll to top whenever the active page changes
    useEffect(() => {
        // Find the scrollable container
        const scrollableContainer = document.querySelector('.overflow-y-auto');
        if (scrollableContainer) {
            scrollableContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [activePage]);
    // Per FILE, not per tab: reset whenever the client file changes (coupleValidationDefaults.ts).
    const [coupleValidation, setCoupleValidation] = useState<CoupleValidationState>(() => freshCoupleValidation());
    
    // Strategy management state - moved to App level (session-based for POC)
    const [savedStrategies, setSavedStrategies] = useState<SavedStrategy[]>(() => {
        // For POC: Clear any existing strategies on app start to ensure fresh session
        // Check both possible keys that might contain data
        const storedStrategies = sessionStorage.getItem('careCompanionStrategies');
        const storedScenarios = sessionStorage.getItem('careCompanionScenarios');
        if (storedStrategies || storedScenarios) {
            // Clear all stored data to ensure fresh start for POC
            sessionStorage.removeItem('careCompanionStrategies');
            sessionStorage.removeItem('careCompanionCaseStudies');
            sessionStorage.removeItem('careCompanionScenarios'); // Also clear this key used by AgedCareSuggestionEnginePanel
            sessionStorage.removeItem('careCompanionReopenStrategy');
        }
        return [];
    });
    const [savedCaseStudies, setSavedCaseStudies] = useState<SavedCaseStudy[]>(() => {
        return [];
    });

    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<GeminiChatTurn[]>([
      { sender: 'bot', text: IN_APP_AI_WELCOME },
    ]);
    const [chatInput, setChatInput] = useState('');
    const [chatSending, setChatSending] = useState(false);

    const submitChatMessage = useCallback(async () => {
      const trimmed = chatInput.trim();
      if (!trimmed || chatSending) return;
      setChatSending(true);
      const history = chatMessages;
      setChatMessages((msgs) => [...msgs, { sender: 'user', text: trimmed }]);
      setChatInput('');
      try {
        // The Aged care module says which of its screens is showing, and the facts on it (UX review,
        // 17 Oct 2026: AWise had no context there at all).
        const agedCareScreen = activePage === 'AgedCarePage' ? getAgedCareScreenContext() : null;
        // The other modules (owner, 22 Oct 2026): the module itself, and the plans on screen with their debts.
        const moduleScreen = getModuleScreenContext(activePage);
        const screenKey =
          activePage === 'Client Profile' ? `factfind-${clientProfileStep}` :
          activePage === 'Dashboard' ? 'dashboard' :
          agedCareScreen ? `aged-care-${agedCareScreen.screen}` :
          moduleScreen ? moduleScreen.screen : (MODULE_SCREEN_KEYS[activePage] ?? null);
        const aiReply = await sendMessageToGemini({
          message: trimmed,
          systemInstruction: buildInAppSystemInstruction(screenKey || '', agedCareScreen?.note ?? moduleScreen?.note ?? null),
          conversationHistory: history,
        });
        setChatMessages((msgs) => [...msgs, { sender: 'bot', text: aiReply }]);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[AgedCareWise AI] send failed', e);
        }
        setChatMessages((msgs) => [
          ...msgs,
          {
            sender: 'bot',
            text: "Something went wrong sending your message. You can try again in a moment.",
          },
        ]);
      } finally {
        setChatSending(false);
      }
    }, [chatInput, chatSending, chatMessages, activePage, clientProfileStep]);

  const {
    professionalRole,
    setProfessionalRole,
    setProfessionalCases,
    scopedProfessionalCases,
    activeProfessionalCaseId,
    setActiveProfessionalCaseId,
    activeProfessionalCase,
    partnerPortalReviewNonce,
    setPartnerPortalReviewNonce,
    partnerIntent,
    setPartnerIntent,
    partnerFirm,
    setPartnerFirm,
    activeRealProfileId,
    setActiveRealProfileId,
    partnerResolving,
    setPartnerResolving,
    portalEntryError,
    setPortalEntryError,
    setPortalRefreshNonce,
    partnerPaywall,
    setPartnerPaywall,
    partnerBilling,
    addClientPaywall,
    setAddClientPaywall,
    showPartnerWizard,
    setShowPartnerWizard,
    updateProfessionalCase,
    markProfessionalCaseSoaStatus,
    setCurrentSoaForCase,
    publishSoaForCase,
    deleteDraftSoa,
    openProfessionalCaseJourney,
    createInPersonProfessionalCase,
    createInviteProfessionalCase,
    submitProfessionalCaseToAdviser,
    registeredSoasByProfileId,
    analyticsDataState,
    loadFactFindsForAnalytics,
    remindClient,
    firmSettings,
    autoReminders,
    messageDefaults,
    saveFirmSettings,
  } = useProfessionalPortal({
    accountUser,
    activePage,
    setActivePage,
    factFind,
    setFactFind,
    clientProfileStep,
    setClientProfileStep,
    setSavedStrategies,
    setCoupleValidation,
    savedCaseStudies,
    setSavedCaseStudies,
    routePlatformAdminWithoutFirm,
  });
  // --- Insight Panel State ---

  /**
   * The planning modules — strategies side by side, charts, multi-year projections. Legible on a
   * laptop or tablet, a squeeze on a phone, which is what SmallScreenNotice says (once) to anyone
   * who opens one on a handset. The client profile is a form and is deliberately absent: it works
   * fine on a phone and now carries its own supporting column there.
   */
  const MODULE_PAGES = new Set([
    'AgedCarePage',
    'HomeCarePage',
    'RetirementVillagePage',
    'GrannyFlatPage',
    'DownsizingPage',
  ]);

  /**
   * Every generated Statement of Advice lands on the server's versioned advice register — immutable,
   * numbered, attributed to the licensee, on the tamper-evident trail, and kept for its retention
   * period. One function for every module (owner, 1 Oct 2026: Support at Home's never reached it).
   *   - A firm working on a real client file: registered against that file.
   *   - A signed-in client on their own file (the direct channel, our licence): they have no file id
   *     in the browser, so it goes through the id-free route — it used to go nowhere at all.
   *   - Anything else (a demo journey, signed out): nothing to register. Returns null.
   */
  const registerGeneratedSoa = (module: AdviceModule, document: Record<string, unknown>) => {
    const request = activeRealProfileId
      ? registerSoa(activeRealProfileId, module, document)
      : accountUser && !activeProfessionalCaseId
        ? registerOwnSoa(module, document)
        : null;
    return request?.catch((err) => {
      if (process.env.NODE_ENV === 'development') console.warn('[soa-register]', err instanceof Error ? err.message : err);
      throw err;
    });
  };

  const renderPageContent = () => (
    <>
      {MODULE_PAGES.has(activePage) && (
        <SmallScreenNotice onBack={() => setActivePage('Dashboard')} />
      )}
      {renderActivePage()}
    </>
  );

  const renderActivePage = () => {
    switch (activePage) {
      case 'Dashboard':
        return <Dashboard
          setActivePage={setActivePage}
          setClientProfileStep={setClientProfileStep}
          factFind={factFind}
          setFactFind={setFactFind}
          savedStrategies={savedStrategies}
          setSavedStrategies={setSavedStrategies}
          savedCaseStudies={savedCaseStudies}
          setSavedCaseStudies={setSavedCaseStudies}
          onDeleteDraftSoa={deleteDraftSoa}
          selectedStrategy={selectedStrategy as { id?: unknown; name?: string } | null}
          setSelectedStrategy={(s) => setSelectedStrategy(s as EvaluatedStrategy | null)}
          professionalContext={activeProfessionalCase ? {
            enabled: true,
            caseId: activeProfessionalCase.id,
            accessMode: activeProfessionalCase.accessMode,
            submissionDate: activeProfessionalCase.submissionDate ?? undefined,
          } : null}
          onSubmitToAdviser={submitProfessionalCaseToAdviser}
        />;
      case 'Client Profile':
                return <ClientProfile
    setActivePage={setActivePage}
    factFind={factFind}
    setFactFind={setFactFind}
    clientProfileStep={clientProfileStep}
    setClientProfileStep={setClientProfileStep}
    hideApplicantContactFields
    showStructureAdviserFields={Boolean(activeProfessionalCaseId)}
/>;
      case 'AgedCarePage':
                return <AgedCarePage
    setActivePage={setActivePage}
    factFind={factFind}
    setFactFind={setFactFind}
    strategyOptions={strategyOptions}
    setStrategyOptions={setStrategyOptions}
    legacyYears={legacyYears}
    setLegacyYears={setLegacyYears}
    selectedStrategy={selectedStrategy}
    setSelectedStrategy={setSelectedStrategy}
    coupleValidation={coupleValidation}
    setCoupleValidation={setCoupleValidation}
    // App.tsx's savedStrategies/savedCaseStudies use the dashboardConfig.tsx shapes (the App-level,
    // cross-feature canonical ones — see Dashboard/RetirementVillagePage); AgedCareAnalyser's own
    // SavedAgedCareStrategy/SavedCaseStudy types are a stricter aged-care-specific view of the same
    // persisted rows. Both sides carry an index signature and only differ in which fields are
    // required, so this is a documented boundary cast, not a real type-safety gap.
    savedStrategies={savedStrategies as unknown as SavedAgedCareStrategy[]}
    setSavedStrategies={setSavedStrategies as unknown as Dispatch<SetStateAction<SavedAgedCareStrategy[]>>}
    savedCaseStudies={savedCaseStudies as unknown as AgedCareSavedCaseStudy[]}
    setSavedCaseStudies={setSavedCaseStudies as unknown as Dispatch<SetStateAction<AgedCareSavedCaseStudy[]>>}
    isProfessionalJourney={Boolean(activeProfessionalCaseId)}
    professionalCaseId={activeProfessionalCaseId}
    onStrategySaved={(strategy) => {
      // REAL client file: persist the modelling to the server so it survives sign-out and so the
      // Partner Portal can see that this client HAS been modelled (the pipeline reads saved_models).
      // Best-effort — the in-app copy in sessionStorage remains the working view either way.
      if (!activeRealProfileId || !strategy?.id) return;
      saveModel(activeRealProfileId, 'aged_care', String(strategy.name || 'Aged care strategy'), strategy as unknown as Record<string, unknown>)
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.warn('[saved-model]', err instanceof Error ? err.message : err);
        });
    }}
    onSoaGenerated={(scenario: AgedCareSavedCaseStudy) => {
      const caseId = String(scenario?.caseId ?? activeProfessionalCaseId ?? '');
      if (caseId) {
        updateProfessionalCase(caseId, { currentSoaId: scenario?.id ? String(scenario.id) : undefined });
        markProfessionalCaseSoaStatus(caseId, 'draft');
      }
      if (scenario?.id) {
        registerGeneratedSoa('aged_care', scenario as unknown as Record<string, unknown>)
          ?.then((reg) => {
            setSavedCaseStudies((prev) =>
              prev.map((sc) =>
                sc.id === scenario.id
                  ? { ...sc, serverSoaId: reg.soaId, serverSoaVersion: reg.version, licenseeName: reg.licenseeName, afslNumber: reg.afslNumber }
                  : sc,
              ),
            );
          })
          .catch((err) => {
            if (process.env.NODE_ENV === 'development') console.warn('[soa-register]', err instanceof Error ? err.message : err);
          });
      }
    }}
    AgedCareAnalyser={AgedCareAnalyser}
/>;
      case 'AssumptionsPage':
        return <AssumptionsPage setActivePage={setActivePage} />;
      case 'HomeCarePage':
        return (
          <SupportAtHomePage
            setActivePage={setActivePage}
            factFind={factFind}
            setFactFind={setFactFind}
            setSavedStrategies={setSavedStrategies as never}
            onSoaGenerated={(document) => { void registerGeneratedSoa('support_at_home', document)?.catch(() => undefined); }}
          />
        );
      /*
       * Its own page key, not a tab inside Support at Home (owner direction, 2026-08-28). The module
       * is deliberately general — it models a client with no care event at all — and since 18 Oct
       * 2026 the client menu and Home open it too. `cameFrom` says which door was used (worked out
       * above from the page before), so its copy and its Back follow the journey without the module
       * knowing anything about Support at Home.
       */
      case 'CashflowPensionPage':
        return <CashflowPensionPage
          setActivePage={setActivePage}
          factFind={factFind}
          onIncomeOnlyUnderstoodChange={incomeOnlySetter(setFactFind)}
          onGuaranteedIncomeGoals={guaranteedIncomeGoalsSetter(setFactFind as never)}
          onCashflowGoals={cashflowGoalsSetter(setFactFind as never)}
          setSavedStrategies={setSavedStrategies as never}
          savedStrategies={savedStrategies as never}
          cameFrom={cashflowCameFromRef.current}
          onSoaGenerated={(document) => { void registerGeneratedSoa('cashflow_pension', document)?.catch(() => undefined); }}
        />;
      case 'RetirementVillagePage':
        // RetirementVillagePage.tsx defines its own local (stricter) SavedStrategy shape — see the
        // AgedCarePage savedStrategies comment above for why this is a documented boundary cast.
        return <RetirementVillagePage
          setActivePage={setActivePage}
          factFind={factFind}
          savedStrategies={savedStrategies as unknown as RvSavedStrategy[]}
          setSavedStrategies={setSavedStrategies as unknown as Dispatch<SetStateAction<RvSavedStrategy[]>>}
          setFactFind={setFactFind}
          onSoaGenerated={(document) => { void registerGeneratedSoa('retirement_village', document)?.catch(() => undefined); }}
        />;
      case 'GrannyFlatPage':
        return (
          <GrannyFlatPage
            setActivePage={setActivePage}
            factFind={factFind}
            savedStrategies={savedStrategies as never}
            setSavedStrategies={setSavedStrategies as never}
            setFactFind={setFactFind}
            onSoaGenerated={(document) => { void registerGeneratedSoa('granny_flat', document)?.catch(() => undefined); }}
          />
        );
      case 'DownsizingPage':
        return (
          <DownsizerPage
            setActivePage={setActivePage}
            factFind={factFind}
            savedStrategies={savedStrategies as never}
            setSavedStrategies={setSavedStrategies as never}
            setFactFind={setFactFind}
            onSoaGenerated={(document) => { void registerGeneratedSoa('downsizer', document)?.catch(() => undefined); }}
          />
        );
      case 'PartnerPortal':
        return <PartnerPortal
          realIdentity={partnerFirm ? partnerFirm.identity : null}
          realModeLoading={(partnerResolving || Boolean(accountUser))}
          role={professionalRole}
          setRole={setProfessionalRole}
          cases={scopedProfessionalCases}
          // dashboardConfig.tsx's SavedCaseStudy (App's canonical shape) is looser than PartnerPortal's
          // own SoaScenario (id/caseId are optional here, required there) — documented boundary cast.
          savedCaseStudies={savedCaseStudies as unknown as SoaScenario[]}
          onCreateInPersonCase={createInPersonProfessionalCase}
          onCreateInviteCase={createInviteProfessionalCase}
          onOpenCaseJourney={openProfessionalCaseJourney}
          reviewNavigationNonce={partnerPortalReviewNonce}
          focusedCaseId={activeProfessionalCaseId}
          onOpenCaseFromModel={(caseId: string) => {
            setActiveProfessionalCaseId(caseId);
            setPartnerPortalReviewNonce((n) => n + 1);
            setActivePage('PartnerPortal');
          }}
          onMarkSoaSent={(caseId: string, soaId: string) => publishSoaForCase(caseId, soaId)}
          onSetCurrentSoaForCase={setCurrentSoaForCase}
          onDeleteDraftSoa={deleteDraftSoa}
          registeredSoasByProfileId={registeredSoasByProfileId}
          analyticsDataState={analyticsDataState}
          onNeedAnalyticsData={loadFactFindsForAnalytics}
          // A reassigned or restricted file changes which rows this adviser may see at all, so the
          // list is re-read from the server rather than patched locally — the server is the one that
          // decides, and guessing here is how a screen starts disagreeing with the database.
          onClientAssignmentChanged={() => setPortalRefreshNonce((n) => n + 1)}
          onRemindClient={remindClient}
          firmSettings={firmSettings}
          autoReminders={autoReminders}
          messageDefaults={messageDefaults}
          onSaveFirmSettings={saveFirmSettings}
          onExitToConsumer={
            partnerFirm
              ? handleSignedOut // real firm member: actually sign out (clears the session)
              : () => {
                  // No firm behind this session: just leave the portal.
                  setActivePage('LandingPage');
                }
          }
          // Platform owner: the Admin entry lives IN the portal sidebar (not a floating pill).
          onOpenAdmin={isPlatformAdmin ? () => setShowAdmin(true) : undefined}
          // Per-client billing (real mode only): plan summary + owner billing actions in Settings.
          // billingApi.ts's PartnerBillingSummary (server response — mode includes 'none', capacity/
          // remaining are nullable) vs PartnerPortal's own display-only BillingSummary (no 'none',
          // no nulls) — documented boundary cast, mirrors the savedCaseStudies one above.
          billingSummary={partnerFirm ? (partnerBilling as unknown as PartnerPortalBillingSummary | null) : null}
          onManageBilling={partnerFirm ? () => openBillingPortal() : undefined}
          onUpgradeAnnual={partnerFirm ? () => startCheckout('partner', partnerFirm.tenantId) : undefined}
          onBuyClient={partnerFirm ? () => startCheckout('partner_client', partnerFirm.tenantId) : undefined}
        />;
      default:
        return <PlaceholderPage title={activePage} setActivePage={setActivePage} />;
    }
  };

  const { flushPendingSave, saveFailure, retrySave, savedElsewhere } = useAutoSave({
    accountUser,
    activeProfessionalCaseId,
    activeRealProfileId,
    activePage,
    factFind,
    clientProfileStep,
    savedStrategies,
    savedCaseStudies,
    coupleValidation,
  });

  const {
    resetToken,
    accountModalMode,
    showAccountModal,
    setShowAccountModal,
    showRegistration,
    setShowRegistration,
    inviteNotice,
    setInviteNotice,
    enterConsumerWithBilling,
    resumeFromSignIn,
    handleSignedOut,
    closeAccountModal,
  } = useSessionAndAuth({
    accountUser,
    setAccountUser,
    activePage,
    setActivePage,
    setFactFind,
    setClientProfileStep,
    setSavedStrategies,
    setSavedCaseStudies,
    setCoupleValidation,
    setShowPaywall,
    partnerIntent,
    setPartnerIntent,
    setActiveProfessionalCaseId,
    setActiveRealProfileId,
    setPartnerFirm,
    setPartnerResolving,
    setPortalRefreshNonce,
    showPartnerWizard,
    setShowPartnerWizard,
    setPartnerPaywall,
    setAddClientPaywall,
    flushPendingSave,
    routePlatformAdminWithoutFirm,
  });

  /**
   * "Get Started" overlay: with real accounts configured this creates an actual account
   * (email + password only — no mobile; we don't collect what we don't use) and drops the new
   * user straight into the fact-find. Demo builds keep the original wireframe modal.
   */
  // NOTE: the platform-admin entry is NEVER a floating pill. It lives in the sidebar's action
  // cluster in the consumer app, and in the portal's own sidebar footer (onOpenAdmin). It is not
  // shown on the public landing page at all.

  /** Invite outcome toast — shown over any screen, dismissible, auto-hides after 12s. */
  const inviteNoticeToast = (
    <>
      {inviteNotice && (
        <div className="fixed top-4 left-1/2 z-[10002] w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
          <div className={`flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-xl ${inviteNotice.tone === 'error' ? 'border-red-200' : 'border-emerald-200'}`}>
            {inviteNotice.tone === 'error'
              ? <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
              : <CheckCircle size={18} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />}
            <p className="flex-1 text-sm leading-relaxed text-gray-700">{inviteNotice.text}</p>
            <button type="button" onClick={() => setInviteNotice(null)} className="shrink-0 rounded p-0.5 text-gray-400 transition hover:text-gray-600" aria-label="Dismiss">
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      {/* A refused save is said on screen, not in the console (owner, 3 Oct 2026). Stays until a save lands. */}
      {saveFailure && (
        <div className="fixed bottom-6 left-1/2 z-[10002] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2" role="alert">
          <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-white px-4 py-3 shadow-xl">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="flex-1 text-sm leading-relaxed text-gray-700"><span className="font-semibold text-gray-900">Your latest changes are not being saved.</span> {saveFailure}</p>
            {/* Saved elsewhere: a retry would be refused again, so the way on is the latest copy (23 Oct 2026). */}
            <button type="button" onClick={savedElsewhere ? () => window.location.reload() : retrySave} className="shrink-0 rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-800" data-testid="save-failure-action">{savedElsewhere ? 'Reload' : 'Try again'}</button>
          </div>
        </div>
      )}
    </>
  );

  const registrationOverlay = showRegistration && (
    <AccountModal
      initialMode="signup"
      factFind={factFind}
      setFactFind={setFactFind}
      onClose={() => setShowRegistration(false)}
      onSignedUp={async () => {
        // See resumeFromSignIn's matching comment: without this, auto-save never enables for a
        // session that just signed up this page load.
        setAccountUser(await getSessionUser());
        setShowRegistration(false);
        setShowGeneralAdvice(null);
        rearmAdviserIntro();
        const inviteToken = takePendingInvite();
        if (inviteToken) {
          const res = await acceptInvite(inviteToken).finally(() => finishInviteRedemption());
          if (res.ok && ['owner', 'adviser', 'staff'].includes(res.role)) {
            setPartnerResolving(true); // branded loader, never the demo login, from frame 1
            setActivePage('PartnerPortal');
            return;
          }
          // A client invite just linked their adviser-prepared file — load it so their details
          // are already there, and so auto-save UPDATES that file instead of orphaning it.
          if (res.ok && res.role === 'client') {
            const loaded = await loadLatestClientProfile().catch(() => null);
            if (loaded) {
              setFactFind(loaded.factFind);
              setClientProfileStep(1);
              setActivePage('Client Profile');
              return;
            }
          }
        }
        // Brand-new direct consumer: pay at Get Started (Option A) if billing is live, otherwise
        // straight into the fact-find. Fact-find starts EMPTY — never data left by a prior session.
        await enterConsumerWithBilling(() => {
          setFactFind(structuredClone(INITIAL_FACT_FIND));
          setClientProfileStep(1);
          setActivePage('Client Profile');
        });
      }}
      onSignedIn={(loaded: LoadedClientProfile | null) => {
        // "Get Started" → "Already have an account? Sign in": a RETURNING user must resume
        // their plan like any other sign-in — never dead-end on the account settings panel.
        setShowRegistration(false);
        setShowGeneralAdvice(null);
        return resumeFromSignIn(loaded);
      }}
    />
  );

  const chatWidget = (
    <>
      {/* Chatbot Button */}
      {/* On a phone held upright, a 48px round button with the icon only, so it does not sit on the
          card (owner-approved polish, 25 Oct 2026); from 640px up, the pill with the wordmark, as before. */}
      <button
        aria-label="Ask AWise"
        className="fixed bottom-4 right-4 h-12 w-12 justify-center p-0 sm:h-auto sm:w-auto sm:justify-start sm:bottom-8 sm:right-8 sm:p-4 sm:short:bottom-5 sm:short:right-5 sm:short:p-3 bg-blue-600 text-white rounded-full shadow-lg z-50 flex items-center gap-2 hover:bg-blue-700 hover:shadow-xl hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        onClick={() => setChatOpen(true)}
      >
        <Bot size={24} className="shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline-flex"><AWiseWordmark size="base" variant="onDark" /></span>
      </button>
      <AgedCareWiseAiChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        messages={chatMessages}
        chatInput={chatInput}
        onChatInputChange={setChatInput}
        onSubmit={submitChatMessage}
        sending={chatSending}
        signedIn={!!accountUser}
        className="z-[9999]"
      />
    </>
  );

  const signedInUserKey = accountUser ? String((accountUser as { id?: unknown; email?: unknown }).id ?? (accountUser as { email?: unknown }).email ?? 'user') : null;

  return (
    <>
    {/* Once, for a signed-in user whose acknowledged privacy policy is older than the current one. */}
    <PrivacyPolicyUpdateNotice userKey={signedInUserKey} />
    {/* Toasts and confirm dialogs, in place of the browser's own alert and confirm boxes. */}
    <NoticeHost />
    <AppRouter
      showGeneralAdvice={showGeneralAdvice}
      setShowGeneralAdvice={setShowGeneralAdvice}
      showSecurity={showSecurity}
      setShowSecurity={setShowSecurity}
      showPrivacy={showPrivacy}
      setShowPrivacy={setShowPrivacy}
      showCost={showCost}
      setShowCost={setShowCost}
      showFaqs={showFaqs}
      setShowFaqs={setShowFaqs}
      showExperience={showExperience}
      setShowExperience={setShowExperience}
      showAdmin={showAdmin}
      setShowAdmin={setShowAdmin}
      isPlatformAdmin={isPlatformAdmin}
      canReviewAdvice={canReviewAdvice}
      showCompliance={showCompliance}
      setShowCompliance={setShowCompliance}
      showApiDocs={showApiDocs}
      setShowApiDocs={setShowApiDocs}
      closeAdmin={closeAdmin}
      adminIsHome={adminIsHome}
      showPaywall={showPaywall}
      partnerPaywall={partnerPaywall}
      activePage={activePage}
      setActivePage={setActivePage}
      renderPageContent={renderPageContent}
      setShowRegistration={setShowRegistration}
      showAccountModal={showAccountModal}
      setShowAccountModal={setShowAccountModal}
      accountModalMode={accountModalMode}
      resetToken={resetToken}
      closeAccountModal={closeAccountModal}
      resumeFromSignIn={resumeFromSignIn}
      handleSignedOut={handleSignedOut}
      setFactFind={setFactFind}
      setAccountUser={setAccountUser}
      showPartnerEntry={showPartnerEntry}
      setShowPartnerEntry={setShowPartnerEntry}
      setActiveProfessionalCaseId={setActiveProfessionalCaseId}
      partnerIntent={partnerIntent}
      setPartnerIntent={setPartnerIntent}
      partnerFirm={partnerFirm}
      setPartnerFirm={setPartnerFirm}
      setProfessionalCases={setProfessionalCases}
      showPartnerWizard={showPartnerWizard}
      setShowPartnerWizard={setShowPartnerWizard}
      setPartnerResolving={setPartnerResolving}
      portalEntryError={portalEntryError}
      setPortalEntryError={setPortalEntryError}
      setPortalRefreshNonce={setPortalRefreshNonce}
      addClientPaywall={addClientPaywall}
      setAddClientPaywall={setAddClientPaywall}
      clientProfileStep={clientProfileStep}
      setClientProfileStep={setClientProfileStep}
      activeProfessionalCaseId={activeProfessionalCaseId}
      factFind={factFind}
      savedStrategies={savedStrategies}
      savedCaseStudies={savedCaseStudies}
      activeProfessionalCaseName={activeProfessionalCase?.clientName ?? null}
      registrationOverlay={registrationOverlay}
      inviteNoticeToast={inviteNoticeToast}
      chatWidget={chatWidget}
    />
    </>
  );
}


// ClientProfile + steps (and their WelcomeInsight/AcatInsightCard) extracted to src/pages/ClientProfile/



// AgedCareAnalyser extracted to src/features/agedCare/AgedCareAnalyser.js
// Aged Care optimisation UI and logic live in AgedCareSuggestionEnginePanel (formerly AgedCareSuggestionEnginePanel).


export default App;
